import React, { useRef, useState, useEffect, useCallback } from 'react'
import { notification, message } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import ReCAPTCHA from 'react-google-recaptcha'
import { motion } from 'framer-motion'

import { Page, Grid, Col, Card, Flex, Divider, Links } from './styles'

import { FiUser, FiLock } from 'react-icons/fi'

import api from '../../services/api'
import { setToken, setUser } from '../../guard'

import logoSrc from '../../assets/images/logo-white.png'
import Input from '../../components/Input'
import Button from '../../components/Button'
import { Helmet } from 'react-helmet'

function Login() {
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [captchaToken, setCaptchaToken] = useState(null)

  const recaptchaRef = React.createRef()

  // Carrega o script do reCAPTCHA
  useEffect(() => {
    // Verificar se o script já existe
    if (!window.grecaptcha && !document.querySelector('script[src*="recaptcha"]')) {
      const script = document.createElement('script')
      script.src = 'https://www.google.com/recaptcha/api.js'
      script.async = true
      script.defer = true
      document.body.appendChild(script)
    }
  }, [])

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token)
  }

  const handleUsernameChange = (e) => {
    setUsername(e.target.value)
  }

  const handlePasswordChange = (e) => {
    setPassword(e.target.value)
  }

  const submitLogin = async () => {
    setLoading(true)

    // Verifica se estamos em ambiente de desenvolvimento
    const isDevelopment = process.env.NODE_ENV === 'development';

    if (!captchaToken && !isDevelopment) {
      notification.error({
        message: 'Erro',
        description: 'Por favor, complete o reCAPTCHA',
      })
      setLoading(false)
      return
    }

    try {
      const response = await api.post('/user/session', {
        username,
        password,
        response: captchaToken || (isDevelopment ? 'dev-mode' : ''),
      })

      const { user, token } = response.data

      setUser(user)
      setToken(token)

      navigate('/dashboard')

      message.success(`Bem-vindo de volta, ${username}`)
    } catch (error) {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset()
      }
      
      if (error.response) {
        notification.error({
          message: 'Revise seus dados',
          description: error.response.data.message,
          placement: 'bottomRight',
        })
      } else {
        message.error('Ops!, não foi possível se conectar com os servidores da atlas plugins.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = useCallback((e) => {
    if (e) {
      e.preventDefault()
    }

    // Verifica se estamos em ambiente de desenvolvimento
    const isDevelopment = process.env.NODE_ENV === 'development';

    if (!username || !password) {
      notification.error({
        message: 'Erro',
        description: 'Por favor, preencha todos os campos.',
      })
      return
    }

    if (!captchaToken && !isDevelopment) {
      notification.error({
        message: 'Erro',
        description: 'Por favor, complete o reCAPTCHA.',
      })
      return
    }

    submitLogin()
  }, [username, password, captchaToken])

  // Animation variants for Framer Motion
  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.2,
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Fazer login - Atlas Plugins</title>
      </Helmet>
      <Page>
        <Grid 
          as={motion.div}
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <Col $left={true}>
            <img src={logoSrc} alt="Atlas Loja - Login" />
            <h1>Faça seu login na plataforma</h1>
          </Col>
          <Col>
            <Card as="form" onSubmit={handleSubmit}>
              <Input
                leftIcon
                Icon={FiUser}
                inputProps={{
                  placeholder: 'Nome de usuário ou endereço de e-mail',
                  id: 'username-field',
                }}
                value={username}
                onChange={handleUsernameChange}
              />
              <Input
                leftIcon
                Icon={FiLock}
                inputProps={{
                  placeholder: 'Informe sua senha',
                  id: 'password-field',
                }}
                isPassword
                value={password}
                onChange={handlePasswordChange}
              />

              <div style={{ marginTop: '15px', marginBottom: '15px' }}>
                {process.env.NODE_ENV === 'development' ? (
                  <div style={{ 
                    padding: '10px', 
                    backgroundColor: '#f0f8ff', 
                    borderRadius: '5px',
                    fontSize: '14px',
                    color: '#666',
                    textAlign: 'center'
                  }}>
                    <p>reCAPTCHA desativado em ambiente de desenvolvimento</p>
                    <small>
                      Em produção, a validação do reCAPTCHA será obrigatória
                    </small>
                  </div>
                ) : (
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                    theme="light"
                  />
                )}
              </div>

              <Link to="/recuperar-senha">Esqueci minha senha</Link>
              <Button
                type="submit"
                loading={loading}
                disabled={!username || !password || (!captchaToken && process.env.NODE_ENV !== 'development')}
              >
                Fazer login
              </Button>
              <Divider />
              <Button 
                onClick={() => navigate('/cadastro')}
                type="button"
              >
                Criar uma nova conta
              </Button>
            </Card>
          </Col>
        </Grid>
      </Page>
    </>
  )
}

export default Login
